import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Input, Button, Tooltip } from "antd";
import { MessageOutlined, MinusOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useHistory } from "react-router-dom";
import { useChatCommandDispatch } from "../../context/ChatCommandContext";

const { TextArea } = Input;

const BASE_URL = 'https://peeky-opus.corelab.com.br'; // process.env.REACT_APP_OPUS_IA_URL || "http://localhost:4000";
const INACTIVITY_TIMEOUT = 10 * 60 * 1000;

const CHUNK_BASE_DELAY  = 600;
const CHUNK_CHAR_DELAY  = 18;
const TYPING_SHOW_MS    = 400;

// Dispara um comando de UI recebido do backend
function dispatchUICommand(cmd, historyRef, dispatchChatCommand) {
  if (!cmd?.type) return;
  if (cmd.type === "navigate") {
    if (cmd.path) historyRef.push(cmd.path);
  } else {
    dispatchChatCommand(cmd);
  }
}

// Gera texto descritivo do que os commands vão fazer
function describeCommands(commands, routes) {
  const parts = [];
  for (const cmd of commands) {
    if (cmd.type === "navigate") {
      const label = routes[cmd.path] || cmd.path;
      parts.push(`ir para **${label}**`);
    } else if (cmd.type === "open_modal" && cmd.modal === "create") {
      parts.push(`abrir formulário de cadastro`);
    } else if (cmd.type === "open_modal" && cmd.modal === "recalculate") {
      parts.push(`abrir recalcular período`);
    } else if (cmd.type === "fill_form") {
      const fields = Object.keys(cmd.fields || {}).join(", ");
      parts.push(`preencher campos: ${fields}`);
    }
  }
  return parts.length ? parts.join(" e ") : "executar ação";
}

// Mapa de path → label para o describeCommands
const ROUTE_LABELS = {
  "/cadastro/employees":    "Funcionários",
  "/cadastro/holidays":     "Feriados",
  "/cadastro/departments":  "Departamentos",
  "/cadastro/functions":    "Funções",
  "/cadastro/journeys":     "Jornadas",
  "/cadastro/justifications": "Justificativas",
  "/cadastro/annual-leave": "Banco de Horas",
  "/gestao/timecard":       "Cartão Ponto",
  "/gestao/dailypoint":     "Ponto Diário",
  "/gestao/lancamentos-coletivos/afastamentos": "Afastamentos",
  "/gestao/lancamentos-coletivos/partial-justifications": "Justificativa Parcial",
  "/gestao/lancamentos-coletivos/manutencao-de-banco-de-horas": "Manutenção Banco de Horas",
  "/relatorios/frequency":  "Relatório Cartão Ponto",
  "/relatorios/geolocation": "Geolocalização",
};

const STYLES = `
  @keyframes dots { 0%,20%{opacity:0} 50%{opacity:1} 100%{opacity:0} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  .chat-bubble p { margin: 0 0 6px; line-height: 1.55; }
  .chat-bubble p:last-child { margin-bottom: 0; }
  .chat-bubble ul, .chat-bubble ol { margin: 4px 0 4px 18px; padding: 0; }
  .chat-bubble li { margin-bottom: 2px; }
  .chat-bubble strong { font-weight: 600; }
  .chat-bubble em { font-style: italic; }
  .chat-bubble code {
    background: rgba(0,0,0,0.07);
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 12px;
    font-family: monospace;
  }
  .chat-bubble pre {
    background: #1e1e2e;
    color: #cdd6f4;
    border-radius: 8px;
    padding: 12px;
    overflow-x: auto;
    font-size: 12px;
    margin: 6px 0;
  }
  .chat-bubble pre code { background: none; padding: 0; color: inherit; }
  .chat-bubble table { border-collapse: collapse; width: 100%; font-size: 12px; margin: 6px 0; }
  .chat-bubble th, .chat-bubble td { border: 1px solid #e0e0e0; padding: 4px 8px; text-align: left; }
  .chat-bubble th { background: #f5f5f5; font-weight: 600; }
  .chat-bubble blockquote {
    border-left: 3px solid #d0d0d0;
    margin: 4px 0;
    padding: 2px 10px;
    color: #666;
    font-style: italic;
  }
  .chat-bubble hr { border: none; border-top: 1px solid #eee; margin: 8px 0; }

  .chat-link-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 7px 14px;
    background: #fff;
    border: 1.5px solid #1890ff;
    border-radius: 20px;
    color: #1890ff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all .18s;
  }
  .chat-link-btn:hover { background: #1890ff; color: #fff; }

  .chat-input:focus { box-shadow: none !important; border-color: #d9d9d9 !important; }
  .chat-send-btn { border-radius: 50% !important; width: 36px !important; height: 36px !important; min-width: 36px !important; padding: 0 !important; display:flex !important; align-items:center !important; justify-content:center !important; }

  .chat-context-menu {
    position: fixed;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    z-index: 9999;
    min-width: 160px;
    overflow: hidden;
    animation: fadeIn .12s ease;
  }
  .chat-context-menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 14px;
    background: none;
    border: none;
    font-size: 13px;
    color: #333;
    cursor: pointer;
    text-align: left;
    transition: background .12s;
  }
  .chat-context-menu button:hover { background: #f5f5f5; }
`;

function LinkButton({ href, children, onNavigate, color }) {
  const isInternal = href && href.startsWith("/");
  if (!isInternal) return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
  return (
    <button
      className="chat-link-btn"
      style={{ borderColor: color, color }}
      onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.color = "#fff"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = color; }}
      onClick={() => onNavigate(href)}
    >
      <span>↗</span> {children || href}
    </button>
  );
}

function MarkdownMessage({ content, onNavigate, color }) {
  return (
    <div className="chat-bubble">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => (
            <LinkButton href={href} onNavigate={onNavigate} color={color}>{children}</LinkButton>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
      <div style={{
        padding: "9px 14px",
        borderRadius: "16px 16px 16px 4px",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        color: "#aaa",
        fontSize: 13,
      }}>
        <span style={{ animation: "dots 1.5s infinite",      display: "inline-block" }}>●</span>{" "}
        <span style={{ animation: "dots 1.5s infinite .3s",  display: "inline-block" }}>●</span>{" "}
        <span style={{ animation: "dots 1.5s infinite .6s",  display: "inline-block" }}>●</span>
      </div>
    </div>
  );
}

function getTextColor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? "#1a1a1a" : "#ffffff";
}

export default function ChatPrompt({ title, provider, token = null, user = {}, client = null, color = "#1890ff", modes = null }) {
  const textColor = getTextColor(color);
  const [input, setInput]               = useState("");
  const [messages, setMessages]         = useState([]);
  const [isOpen, setIsOpen]             = useState(false);
  const [isMinimized, setIsMinimized]   = useState(false);
  const [isLoading, setIsLoading]       = useState(false);
  const [isTyping, setIsTyping]         = useState(false);
  const [pendingLink, setPendingLink]   = useState(null);
  const [pendingCommands, setPendingCommands] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, content }
  const conversationIdRef = useRef(null);
  const messagesEndRef  = useRef(null);
  const inactivityTimer = useRef(null);
  const chunkTimers     = useRef([]);
  const history = useHistory();
  const dispatchChatCommand = useChatCommandDispatch();

  // Fecha context menu ao clicar fora
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [contextMenu]);

  const handleContextMenu = (e, content) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, content });
  };

  const handleRetrySend = () => {
    if (!contextMenu) return;
    setInput(contextMenu.content);
    setContextMenu(null);
  };

  // Escuta evento byponto:minimize — telas podem pedir para minimizar o chat
  useEffect(() => {
    const onMinimize = () => setIsMinimized(true);
    const onRestore  = () => { setIsOpen(true); setIsMinimized(false); };
    window.addEventListener("byponto:chat:minimize", onMinimize);
    window.addEventListener("byponto:chat:restore",  onRestore);
    return () => {
      window.removeEventListener("byponto:chat:minimize", onMinimize);
      window.removeEventListener("byponto:chat:restore",  onRestore);
    };
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setMessages([]);
      setIsOpen(false);
      setIsMinimized(false);
    }, INACTIVITY_TIMEOUT);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetTimer();
      if (!conversationIdRef.current) {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        conversationIdRef.current = id;
        setConversationId(id);
      }
    } else {
      clearTimeout(inactivityTimer.current);
    }
    return () => clearTimeout(inactivityTimer.current);
  }, [isOpen, resetTimer]);


  useEffect(() => {
    if (!isMinimized) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, isMinimized]);

  // Limpa timers de chunk ao desmontar
  useEffect(() => () => chunkTimers.current.forEach(clearTimeout), []);

  const deliverChunks = useCallback((chunks) => {
    setIsTyping(true);
    let accumulated = 0;

    chunks.forEach((chunk, idx) => {
      // Tempo de "digitação" proporcional ao tamanho do chunk anterior
      const prevLen   = idx === 0 ? 0 : chunks[idx - 1].length;
      const typingMs  = idx === 0 ? 0 : TYPING_SHOW_MS + Math.min(prevLen * CHUNK_CHAR_DELAY, 2000);
      const showAt    = accumulated + typingMs + (idx === 0 ? 0 : CHUNK_BASE_DELAY);
      accumulated     = showAt;

      const t = setTimeout(() => {
        setMessages(prev => [...prev, { role: "assistant", content: chunk }]);
        if (idx === chunks.length - 1) setIsTyping(false);
      }, showAt);

      chunkTimers.current.push(t);
    });
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || isTyping) return;
    resetTimer();
    chunkTimers.current.forEach(clearTimeout);
    chunkTimers.current = [];

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const { data } = await axios.post(`${BASE_URL}/${provider}/chat`, {
        question:        userMessage,
        user,
        client,
        access_token:    token || "54450079cb044395986752ff0e90c400",
        modes:           modes || ["orientation"],
        conversationId:  conversationIdRef.current,
      });

      setIsLoading(false);
      const chunks = data.chunks?.length ? data.chunks : [data.response];

      // Todos os commands ficam pendentes — usuário confirma antes de executar
      if (data.commands?.length) {
        setPendingCommands(data.commands);
      }

      deliverChunks(chunks);
    } catch {
      setIsLoading(false);
      setMessages(prev => [...prev, { role: "assistant", content: "Erro ao buscar resposta da API." }]);
    }
  };

  const handleNavigate = (path) => setPendingLink(path);

  const confirmNavigate = () => {
    if (pendingLink) { history.push(pendingLink); setPendingLink(null); }
  };

  // Executa commands pendentes: navega, abre modal, preenche form — depois minimiza
  const confirmCommands = () => {
    pendingCommands.forEach(cmd => dispatchUICommand(cmd, history, dispatchChatCommand));
    setPendingCommands([]);
    const hasUiAction = pendingCommands.some(c => c.type !== "navigate");
    if (hasUiAction) setIsMinimized(true);
  };

  const cancelCommands = () => setPendingCommands([]);

  const handleClose = () => {
    chunkTimers.current.forEach(clearTimeout);
    chunkTimers.current = [];
    conversationIdRef.current = null;
    setConversationId(null);
    setIsOpen(false);
    setIsMinimized(false);
    setMessages([]);
    setIsTyping(false);
    setPendingLink(null);
    clearTimeout(inactivityTimer.current);
  };

  const showTypingBubble = isLoading || isTyping;

  return (
    <>
      <style>{STYLES}</style>

      {!isOpen && (
        <div style={{ position: "fixed", bottom: 28, right: 28, zIndex: 1000 }}>
          <Button
            type="primary" shape="circle"
            icon={<MessageOutlined style={{ fontSize: 20 }} />}
            onClick={() => setIsOpen(true)}
            style={{ width: 52, height: 52, background: color, borderColor: color, boxShadow: `0 4px 16px ${color}55` }}
          />
        </div>
      )}

      {isOpen && (
        <div style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 1000,
          width: 380, borderRadius: 16,
          boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
          backgroundColor: "#fff", overflow: "hidden",
          border: "1px solid #f0f0f0", animation: "fadeIn .2s ease",
        }}>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 16px",
            background: `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: "#52c41a", boxShadow: "0 0 0 2px rgba(82,196,26,0.3)",
              }} />
              <div>
                <span style={{ color: textColor, fontWeight: 600, fontSize: 14 }}>{title}</span>
                {conversationId && (
                  <div style={{ color: textColor, opacity: 0.6, fontSize: 10, marginTop: 1 }}>#{conversationId}</div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <Tooltip title={isMinimized ? "Expandir" : "Minimizar"}>
                <Button type="text" size="small" icon={<MinusOutlined />}
                  onClick={() => setIsMinimized(v => !v)}
                  style={{ color: textColor, opacity: 0.85 }} />
              </Tooltip>
              <Tooltip title="Fechar">
                <Button type="text" size="small" icon={<CloseOutlined />}
                  onClick={handleClose}
                  style={{ color: textColor, opacity: 0.85 }} />
              </Tooltip>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div style={{ height: 360, overflowY: "auto", padding: "16px 14px", background: "#f7f8fc" }}>
                {messages.length === 0 && !showTypingBubble && (
                  <div style={{ textAlign: "center", color: "#bbb", fontSize: 13, marginTop: 80 }}>
                    <MessageOutlined style={{ fontSize: 28, marginBottom: 8, display: "block" }} />
                    Como posso ajudar?
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} style={{
                    marginBottom: 10, display: "flex", flexDirection: "column",
                    alignItems: msg.role === "user" ? "flex-end" : "flex-start",
                    animation: "fadeIn .2s ease",
                  }}>
                    <div
                      onContextMenu={msg.role === "user" ? (e) => handleContextMenu(e, msg.content) : undefined}
                      style={{
                        maxWidth: "82%", padding: "9px 13px",
                        borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        background: msg.role === "user" ? `linear-gradient(135deg, ${color}, ${color}bb)` : "#fff",
                        color: msg.role === "user" ? textColor : "#222",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        fontSize: 13, lineHeight: 1.5,
                        cursor: msg.role === "user" ? "context-menu" : "default",
                      }}>
                      {msg.role === "user" ? (
                        <span style={{ whiteSpace: "pre-wrap" }}>{msg.content}</span>
                      ) : (
                        <MarkdownMessage content={msg.content} onNavigate={handleNavigate} color={color} />
                      )}
                      {msg.link && (
                        <button className="chat-link-btn"
                          style={{ borderColor: color, color, marginTop: 10 }}
                          onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = color; }}
                          onClick={() => handleNavigate(msg.link)}>
                          <span>↗</span> Ir para a página
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {showTypingBubble && <TypingIndicator />}

                <div ref={messagesEndRef} />
              </div>

              {/* Confirm navigation banner */}
              {pendingLink && (
                <div style={{
                  padding: "10px 14px", background: `${color}11`,
                  borderTop: `1px solid ${color}33`,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 8, fontSize: 13,
                }}>
                  <span style={{ color }}>Ir para <strong>{pendingLink}</strong>?</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button size="small" onClick={() => setPendingLink(null)}>Não</Button>
                    <Button size="small" type="primary" onClick={confirmNavigate}>Ir</Button>
                  </div>
                </div>
              )}

              {/* Confirm UI commands banner */}
              {pendingCommands.length > 0 && (
                <div style={{
                  padding: "10px 14px", background: `${color}11`,
                  borderTop: `1px solid ${color}33`,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 8, fontSize: 13,
                }}>
                  <span style={{ color, flex: 1 }}>
                    ✨ Deseja {describeCommands(pendingCommands, ROUTE_LABELS)}?
                  </span>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <Button size="small" onClick={cancelCommands}>Não</Button>
                    <Button size="small" type="primary" onClick={confirmCommands}>Sim</Button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div style={{ padding: "10px 12px", borderTop: "1px solid #f0f0f0", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                  <TextArea
                    className="chat-input"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={input}
                    onChange={e => { setInput(e.target.value); resetTimer(); }}
                    placeholder="Digite uma mensagem..."
                    onPressEnter={e => { if (!e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    style={{
                      flex: 1, resize: "none", borderRadius: 20,
                      padding: "7px 14px", fontSize: 13,
                      border: "1px solid #e8e8e8", background: "#f7f8fc",
                    }}
                  />
                  <Button
                    className="chat-send-btn"
                    type="primary"
                    icon={<SendOutlined style={{ fontSize: 14 }} />}
                    onClick={sendMessage}
                    loading={isLoading}
                    disabled={isLoading || isTyping || !input.trim()}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {contextMenu && (
        <div
          className="chat-context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={e => e.stopPropagation()}
        >
          <button onClick={handleRetrySend}>
            ↩ Reenviar mensagem
          </button>
        </div>
      )}
    </>
  );
}
